import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MainLayout } from '../components/Layout/MainLayout';
import {
  ProtectedRoute,
  FORCE_PASSWORD_CHANGE_PATH,
} from '../components/Auth/ProtectedRoute';
import { ForcePasswordChange } from '../pages/Auth/ForcePasswordChange';
import { Dashboard } from '../pages/Dashboard';
import { Customers } from '../pages/Customers/Customers.tsx';
import { CustomerDetail } from '../pages/Customers/CustomerDetail.tsx';
import Services from '../pages/Services/Services.tsx';
import PriceIndexHistory from '../pages/PriceIndexHistory/PriceIndexHistory.tsx';
import PriceIndexPreview from '../pages/PriceIndexHistory/PriceIndexPreview.tsx';
import WorkScheduleModelsPage from '../pages/WorkScheduleModels/WorkScheduleModelsPage.tsx';
import VehiclesPage from '@/pages/Vehicles/VehiclesPage';
import VehicleLogbookPage from '@/pages/VehicleLogbook/VehicleLogbookPage';
import OpenItemsPage from '@/pages/OpenItems/OpenItemsPage';
import TasksPage from '../pages/Tasks/TasksPage';
import { TaskDetail } from '../pages/Tasks/TaskDetail.tsx';
import ChecklistProtocolPage from '../pages/ChecklistProtocol/ChecklistProtocolPage';
import MyChecklistsPage from '../pages/MyChecklists/MyChecklistsPage';
import ObjectsPage from '../pages/Objects/ObjectsPage';
import ObjectDetailPage from '../pages/Objects/ObjectDetailPage';
import NetzplanImportPage from '../pages/Import/NetzplanImportPage';
import ContractImportPage from '../pages/Import/Contract/ContractImportPage';
import OffersPage from '../pages/Offers/OffersPage';
import OfferCreatePage from '../pages/Offers/OfferCreatePage';
import OfferDetailPage from '../pages/Offers/OfferDetailPage';
import InvoicesPage from '../pages/Invoices/InvoicesPage';
import InvoiceCreatePage from '../pages/Invoices/InvoiceCreatePage';
import InvoiceManualCreatePage from '../pages/Invoices/InvoiceManualCreatePage';
import InvoiceDetailPage from '../pages/Invoices/InvoiceDetailPage';
import Planning from '../pages/Planning/Planning';
import PlanningAssistantPage from '../pages/Planning/Assistant/PlanningAssistantPage';
import { Employees } from '../pages/Employees/Employees.tsx';
import { EmployeeDetail } from '../pages/Employees/EmployeeDetail.tsx';
import AbsencesPage from '../pages/Absences/AbsencesPage.tsx';
import MyAbsencesPage from '../pages/Absences/MyAbsencesPage.tsx';
import { Profile } from '../pages/Profile/Profile.tsx';
import ReleaseNotesPage from '../pages/Settings/ReleaseNotes/ReleaseNotesPage';
import { SuperAdminBlock } from '../components/Auth/SuperAdminBlock';
import { Login } from '../pages/Auth/Login.tsx';
import { ForgotPassword } from '../pages/Auth/ForgotPassword.tsx';
import { ResetPassword } from '../pages/Auth/ResetPassword.tsx';
import TenantsPage from '../pages/Tenants/TenantsPage';
import { AdminShell } from '../components/Layout/AdminShell';
import TenantsAdminPage from '../pages/Admin/Tenants/TenantsAdminPage';
import TenantAdminDetailPage from '../pages/Admin/Tenants/TenantAdminDetailPage';
import SubscriptionPlansPage from '../pages/Admin/SubscriptionPlans/SubscriptionPlansPage';
import TenantInvoicesPage from '../pages/Admin/TenantInvoices/TenantInvoicesPage';
import TenantInvoiceDetailPage from '../pages/Admin/TenantInvoices/TenantInvoiceDetailPage';
import SupportInboxPage from '../pages/Admin/Support/SupportInboxPage';
import DemoDataAdminPage from '../pages/Admin/DemoData/DemoDataAdminPage';
import SupportTicketDetailPage from '../pages/Admin/Support/SupportTicketDetailPage';
import PricingPage from '../pages/Public/PricingPage';
import ContactPage from '../pages/Public/ContactPage';
import TimeClockPage from '../pages/TimeClock/TimeClockPage';
import TimeClockAdminPage from '../pages/TimeClock/TimeClockAdminPage';
import MyPlanPage from '../pages/MyPlan/MyPlanPage';
import SettingsPage from '../pages/Settings/SettingsPage';
import TextSnippetsPage from '../pages/Settings/TextSnippets/TextSnippetsPage';
import TenantSettingsPage from '../pages/Settings/TenantSettings/TenantSettingsPage';
import AbsenceSettingsPage from '../pages/Settings/AbsenceSettings/AbsenceSettingsPage';
import TimeClockGroupsPage from '../pages/Settings/TimeClockGroups/TimeClockGroupsPage';
import MySubscriptionPage from '../pages/Settings/Subscription/MySubscriptionPage';
import MeSupportInboxPage from '../pages/Support/MeSupportInboxPage';
import MeSupportTicketDetailPage from '../pages/Support/MeSupportTicketDetailPage';
import { useAuthStore } from '../store/useAuthStore';

export function AppRouter() {
  const { isAuthenticated, isSuperAdmin } = useAuthStore();
  // SuperAdmins always default-land on the admin cockpit, even when /login
  // is re-visited after auth.
  const defaultLanding = isSuperAdmin ? '/admin/tenants' : '/';

  return (
    <BrowserRouter>
      <Routes>
        {/* Public Route - Login */}
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to={defaultLanding} replace /> : <Login />}
        />

        {/* Authenticated but blocked: the account carries a server-assigned password.
            Wrapped in ProtectedRoute so an anonymous visitor still gets the login screen;
            the guard lets this one path through. */}
        <Route
          path={FORCE_PASSWORD_CHANGE_PATH}
          element={
            <ProtectedRoute>
              <ForcePasswordChange />
            </ProtectedRoute>
          }
        />

        {/* Public Routes - Password reset (self-service, no auth) */}
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Anonymous public marketing routes */}
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/kontakt" element={<ContactPage />} />

        {/* SaaS-Owner Admin-Cockpit (separate shell, SuperAdmin only) */}
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute allowedRoles={['SuperAdmin']}>
              <AdminShell>
                <Routes>
                  <Route index element={<Navigate to="tenants" replace />} />
                  <Route path="tenants" element={<TenantsAdminPage />} />
                  <Route path="tenants/:id" element={<TenantAdminDetailPage />} />
                  <Route path="subscription-plans" element={<SubscriptionPlansPage />} />
                  <Route path="tenant-invoices" element={<TenantInvoicesPage />} />
                  <Route path="tenant-invoices/:id" element={<TenantInvoiceDetailPage />} />
                  <Route path="support" element={<SupportInboxPage />} />
                  <Route path="support/:id" element={<SupportTicketDetailPage />} />
                  <Route path="demo-data" element={<DemoDataAdminPage />} />
                  <Route path="profile" element={<Profile />} />
                  <Route path="release-notes" element={<ReleaseNotesPage />} />
                </Routes>
              </AdminShell>
            </ProtectedRoute>
          }
        />

        {/* Protected Routes - All require authentication.
            SuperAdmin is force-routed to /admin/tenants so the tenant layout
            never renders for them. */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <SuperAdminBlock>
              <MainLayout>
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  
                  {/* SuperAdmin-only routes — kept for bookmark compatibility, redirected to admin cockpit */}
                  <Route
                    path="/tenants"
                    element={<Navigate to="/admin/tenants" replace />}
                  />
                  <Route
                    path="/tenants/legacy"
                    element={
                      <ProtectedRoute allowedRoles={['SuperAdmin']}>
                        <TenantsPage />
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* Admin-only routes */}
                  <Route 
                    path="/customers" 
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <Customers />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/customers/:id" 
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <CustomerDetail />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/services" 
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <Services />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/price-indexes/history" 
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <PriceIndexHistory />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/price-indexes/:id/preview" 
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <PriceIndexPreview />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/work-schedule-models" 
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <WorkScheduleModelsPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route
                    path="/vehicles"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <VehiclesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/vehicles/logbook"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <VehicleLogbookPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/tasks" 
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <TasksPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/tasks/:id" 
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <TaskDetail />
                      </ProtectedRoute>
                    } 
                  />
                  {/* Employee working surface — no allowedRoles, the server scopes to the caller. */}
                  <Route path="/my-checklists" element={<MyChecklistsPage />} />
                  {/* Open to all roles — the server scopes the list and rejects scope=all for employees. */}
                  <Route path="/open-items" element={<OpenItemsPage />} />
                  <Route
                    path="/checklist-protocol"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <ChecklistProtocolPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/employees"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <Employees />
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/employees/:id" 
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <EmployeeDetail />
                      </ProtectedRoute>
                    } 
                  />
                  <Route
                    path="/objects"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <ObjectsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/objects/:id"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <ObjectDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/import/netzplan"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <NetzplanImportPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/import/contract"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <ContractImportPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/offers"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <OffersPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/offers/new"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <OfferCreatePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/offers/:id"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <OfferDetailPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/invoices"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <InvoicesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/invoices/new"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <InvoiceCreatePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/invoices/new/manual"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <InvoiceManualCreatePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/invoices/:id"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <InvoiceDetailPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Shared routes - both roles */}
                  <Route path="/planning" element={<Planning />} />
                  <Route
                    path="/planning/assistant"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung', 'SuperAdmin']}>
                        <PlanningAssistantPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/my-plan" element={<MyPlanPage />} />
                  <Route path="/absences" element={<MyAbsencesPage />} />
                  <Route
                    path="/absences/admin"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <AbsencesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/timeclock" element={<TimeClockPage />} />
                  <Route
                    path="/timeclock/admin"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <TimeClockAdminPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/profile" element={<Profile />} />
                  <Route
                    path="/release-notes"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <ReleaseNotesPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <SettingsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings/text-snippets"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <TextSnippetsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings/tenant"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <TenantSettingsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings/absences"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <AbsenceSettingsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings/timeclock-groups"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <TimeClockGroupsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings/subscription"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <MySubscriptionPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/support"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <MeSupportInboxPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/support/:id"
                    element={
                      <ProtectedRoute allowedRoles={['Verwaltung']}>
                        <MeSupportTicketDetailPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Unauthorized page */}
                  <Route 
                    path="/unauthorized" 
                    element={
                      <div className="flex items-center justify-center min-h-[60vh]">
                        <div className="text-center">
                          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                            Zugriff verweigert
                          </h1>
                          <p className="text-gray-600 dark:text-gray-400 mb-8">
                            Sie haben keine Berechtigung, diese Seite zu sehen.
                          </p>
                          <a 
                            href="/" 
                            className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                          >
                            Zurück zum Dashboard
                          </a>
                        </div>
                      </div>
                    } 
                  />
                </Routes>
              </MainLayout>
              </SuperAdminBlock>
            </ProtectedRoute>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
